import fetch from 'isomorphic-fetch'

export const REST_CALL_STATUS = {
  NONE: 'none',
  FETCHING: 'fetching',
  RECEIVED: 'received',
  UPLOADING: 'uploading',
  ERROR: 'error',
  RESET: 'reset'
}

const _callH2Server = (actionArgs, fetchCall, dispatch) => {
  let theResponse = null

  dispatch({ restCall: { status: REST_CALL_STATUS.FETCHING }, ...actionArgs })

  return fetchCall()
    .then(response => {
      theResponse = response

      if (!response.ok) {
        //clone the response so it can be consumed later (otherwise it will be locked)
        response
          .clone()
          .text()
          .then(resp => console.log(`API request failed: ${resp}`))
        throw `An error occurred, ${response.statusText}`
      } else {
        const contentType = response.headers.get('content-type')

        if (contentType && contentType.indexOf('application/json') !== -1) {
          return response.json().then(body => ({ response, body }))
        }

        return { response }
      }
    })
    .then(({ response, body }) => {
      dispatch({
        restCall: { status: REST_CALL_STATUS.RECEIVED, response, result: body },
        ...actionArgs
      })

      return body
    })
    .catch(error => {
      console.log('Error occurred during fetch', error)
      dispatch({
        restCall: { status: REST_CALL_STATUS.ERROR, response: theResponse, error },
        ...actionArgs
      })
      throw { error, response: theResponse }
    })
}

export const apiPrefix = '/api/v1'
const v2ApiPrefix = '/api/v2'

export const fetchApi = (uri, method = 'GET', arg = null, jsonBody = false) => {
  const filterNullKeys = (key, value) => {
    if (value === null) {
      return undefined
    }

    return value
  }
  const requestArg = jsonBody
    ? {
        method,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(arg, filterNullKeys)
      }
    : {
        method
      }

  requestArg.credentials = 'same-origin'

  const prefixedUrl =
    uri.startsWith(apiPrefix) || uri.startsWith(v2ApiPrefix) ? uri : apiPrefix + uri

  return fetch(prefixedUrl, requestArg)
}

const _fetchFile = (uri, method, body, actionArgs, dispatch) => {
  const _fetch = (url, opts = {}) => {
    return new Promise((res, rej) => {
      const xhr = new XMLHttpRequest()

      xhr.responseType = 'json'

      for (const k in opts.headers || {}) {
        xhr.setRequestHeader(k, opts.headers[k])
      }

      xhr.onerror = e => {
        rej(
          dispatch({
            restCall: {
              status: REST_CALL_STATUS.ERROR,
              e,
              result: e.target.response
            },
            ...actionArgs
          })
        )
      }
      xhr.addEventListener('load', e => {
        const result = e.target.response

        if (xhr.status < 400) {
          dispatch({
            restCall: {
              status: REST_CALL_STATUS.RECEIVED,
              e,
              result
            },
            ...actionArgs
          })
          res(result)
        } else {
          rej(
            dispatch({
              restCall: {
                status: REST_CALL_STATUS.ERROR,
                e,
                result
              },
              ...actionArgs
            })
          )
        }
      })
      xhr.upload.addEventListener('progress', event => {
        dispatch({
          restCall: {
            status: REST_CALL_STATUS.UPLOADING,
            complete: Math.round((event.loaded / event.total) * 100)
          },
          ...actionArgs
        })
      }) // event.loaded / event.total * 100 ; //event.lengthComputable
      xhr.open(opts.method || 'get', url)
      xhr.send(opts.body)
    })
  }
  const requestArg = {
    method,
    credentials: 'same-origin',
    body
  }
  const prefixedUrl =
    uri.startsWith(apiPrefix) || uri.startsWith(v2ApiPrefix) ? uri : apiPrefix + uri

  return _fetch(prefixedUrl, requestArg)
}

export const receivedResponse = actionOrRequest => {
  const restCall = actionOrRequest.restCall || actionOrRequest

  return (
    restCall?.status === REST_CALL_STATUS.RECEIVED ||
    restCall?.status === REST_CALL_STATUS.ERROR
  )
}

export const isSuccess = actionOrRequest => {
  const restCall = actionOrRequest.restCall || actionOrRequest

  return restCall?.status === REST_CALL_STATUS.RECEIVED
}

export const getResult = actionOrRequest => {
  const restCall = actionOrRequest.restCall || actionOrRequest

  return restCall?.result
}

const restCallError = (action, params, response) => {
  let errorMessage = null
  const errorFn = action.errorFn || params.errorFn

  if (errorFn) {
    errorMessage = errorFn(response)
  }

  if (!errorMessage) {
    if (!response) {
      errorMessage = 'Could not contact server'
    } else {
      errorMessage = action.error || params.error || 'An error occurred'
    }
  }

  return errorMessage
}

export const handleRest = (action, params = {}, state) => {
  const none = {
    status: REST_CALL_STATUS.NONE,
    origin: {}, //what action initiated this request?
    loading: false,
    error: null,
    result: params.result //default value
  }

  if (action && action.restCall) {
    const restCall = action.restCall
    const status = restCall.status
    const result = restCall.result

    //console.log("result", action.result, "restCall", restCall);

    switch (status) {
      case REST_CALL_STATUS.RESET: {
        return none
      }

      case REST_CALL_STATUS.ERROR: {
        return {
          ...state,
          status,
          loading: false,
          error: restCallError(action, params, restCall.response)
        }
      }

      case REST_CALL_STATUS.RECEIVED: {
        return {
          ...state,
          status,
          result,
          loading: false,
          error: null
        }
      }

      /*
       * Don't reset result until a response
       * comes in or pages like listings may break
       */
      case REST_CALL_STATUS.UPLOADING:
      case REST_CALL_STATUS.FETCHING: {
        return {
          ...state,
          status,
          origin: action,
          loading: true,
          error: null
        }
      }

      default: {
        return none
      }
    }
  } else {
    return none
  }
}

export const initialRestState = (params = {}) => handleRest(null, params)

export const restReducer = (actionType, params = {}) => (
  state = initialRestState(params),
  action
) => {
  //Allows this reducer to only respond to some actions of the given type
  const actionFilter =
    (params && params.actionFilter) ||
    (() => {
      return true
    })
  const nextState = state

  if (action.type === actionType && actionFilter(action)) {
    return handleRest(action, params, state)
  }

  //console.log("Ran rest reducer for ", actionType, "responding with", nextState);

  return nextState
}

export const get = uri => actionArgs => dispatch => {
  return _callH2Server(actionArgs, () => fetchApi(uri, 'GET', null, false), dispatch)
}

export const post = (uri, arg) => actionArgs => dispatch => {
  return _callH2Server(actionArgs, () => fetchApi(uri, 'POST', arg, true), dispatch)
}

export const postFile = (uri, arg) => actionArgs => dispatch => {
  return _fetchFile(uri, 'POST', arg, actionArgs, dispatch)
}

export const putFile = (uri, arg) => actionArgs => dispatch => {
  return _fetchFile(uri, 'PUT', arg, actionArgs, dispatch)
}

export const put = (uri, arg) => actionArgs => dispatch => {
  return _callH2Server(actionArgs, () => fetchApi(uri, 'PUT', arg, true), dispatch)
}

export const doDelete = uri => actionArgs => dispatch => {
  return _callH2Server(actionArgs, () => fetchApi(uri, 'DELETE', {}, true), dispatch)
}

export const resetRequest = actionArgs => dispatch => {
  return dispatch({
    ...actionArgs,
    restCall: {
      status: REST_CALL_STATUS.RESET
    }
  })
}
